import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Schedule as ScheduleIcon,
  Bolt as BoltIcon,
  CheckCircle as CheckCircleIcon,
  RocketLaunch as RocketLaunchIcon,
  Warning as WarningIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import {
  Announcement,
  announcementService,
} from '../services/announcementService';
import {
  categoryToLabel,
  categoryToSeverity,
  formatRomeDateTime,
  statusToChipColor,
  statusToLabel,
} from './announcement-mappings';

const POLL_INTERVAL_MS = 60_000;

const CATEGORY_ICON: Record<Announcement['category'], React.ReactElement> = {
  deploy: <RocketLaunchIcon fontSize="inherit" />,
  patch_critica: <WarningIcon fontSize="inherit" />,
  manutenzione: <BuildIcon fontSize="inherit" />,
};

const STATUS_ICON: Record<Announcement['status'], React.ReactElement> = {
  programmato: <ScheduleIcon fontSize="small" />,
  in_corso: <BoltIcon fontSize="small" />,
  completato: <CheckCircleIcon fontSize="small" />,
};


const HomeAnnouncementBanner: React.FC = () => {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [expanded, setExpanded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const next = await announcementService.getActive();
      setAnnouncement(next);
      if (!next) setExpanded(false);
    } catch {
      // Silent: banner is opportunistic. The next tick retries.
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(refresh, POLL_INTERVAL_MS);
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  if (!announcement) return null;

  const hasDetails = !!(announcement.detailsMd || announcement.softwareVersion);

  return (
    <Alert
      severity={categoryToSeverity[announcement.category]}
      icon={CATEGORY_ICON[announcement.category]}
      sx={{ mb: 3 }}
    >
      <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <span>{announcement.title}</span>
        <Chip
          size="small"
          color={statusToChipColor[announcement.status]}
          icon={STATUS_ICON[announcement.status]}
          label={statusToLabel[announcement.status]}
        />
        <Typography variant="caption" sx={{ ml: 'auto', opacity: 0.85 }}>
          {categoryToLabel[announcement.category]} ·{' '}
          {formatRomeDateTime(announcement.startAt)}
          {announcement.downtimeEstimate ? ` · ${announcement.downtimeEstimate}` : ''}
        </Typography>
      </AlertTitle>

      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {announcement.userImpact}
      </Typography>

      {hasDetails && (
        <Box sx={{ mt: 1 }}>
          <IconButton
            size="small"
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
            aria-label="Mostra dettagli"
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography
            variant="caption"
            sx={{ ml: 0.5, cursor: 'pointer' }}
            onClick={() => setExpanded((e) => !e)}
          >
            Dettagli
          </Typography>
          <Collapse in={expanded}>
            <Box sx={{ mt: 1, pl: 4 }}>
              {announcement.detailsMd && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {announcement.detailsMd}
                </Typography>
              )}
              {announcement.softwareVersion && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                >
                  Versione: {announcement.softwareVersion}
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>
      )}
    </Alert>
  );
};

export default HomeAnnouncementBanner;
